import { hexToRgb } from 'assets/jss/material-dashboard-pro-react';
import React from 'react';
import { HIGH_PROPENSITY_TO_SELL_PROPERTY_FILTER_PRESET } from 'types/campaign';

import { makeStyles, useTheme } from '@material-ui/core/styles';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';

import CampaignInfoCards from 'components/CampaignInfoCards/CampaignInfoCards';
import DashboardBubble from 'components/DashboardBubble/DashboardBubble';
import { useDataFilterContext } from 'components/DataMap/DataFilterProvider';
import DataMap from 'components/DataMap/DataMap';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';

import { getDynamicDataCount } from 'utils/api';
import {
  calculateCampaignDates,
  calculateCampaignPrices,
  isHighPropensityToSellSupported,
} from 'utils/lib';

const useStyles = makeStyles(() => ({
  map: {
    minHeight: '82vh',
    display: 'flex',
    flexDirection: 'column',
    margin: '0 0 20px',
  },
  infoCards: {
    margin: '0 0 50px',
  },
}));

const addOnPropertiesCountName = 'Add On Properties';
const defaultFiltersModalOptions = {
  selectPropertyTypesByDefault: ['SFR', 'Condominium', 'Townhouse', 'Multi Family Dwelling', 'Apartment Building', 'Duplex', 'Triplex', 'Quadplex', 'Mid Rise Condo', 'High Rise Condo'],
  disableEventTypeSelector: true,
};

const getCountsTotal = (counts) => counts.reduce((sum, { count }) => sum + count, 0);

const defaultFetchDataCount = async ({
  eventTypeId, propertyFilters, polygonPaths, zipcode,
}) => getDynamicDataCount(eventTypeId, propertyFilters, polygonPaths, zipcode);

const CampaignTargetingSection = React.forwardRef(({
  selectedEventType,
  customMonthlyRatePerApn,
  isAnnualBilling,
  setIsAnnualBilling,
  extraMailOutsCount = 0,
  numApnsAlreadyBeingFarmed = 0,
  renewalPreviousMonthlyRatePerApn,
  estimatedNumberOfApnsOverride,
  initPropertyFilters = null,
  initSelectionPolygonPaths = [],
  initSelectedZipcodes = [],
  preferredUseHighPropensityToSell = null,
  filtersModalOptions = defaultFiltersModalOptions,
  inputLabelText,
  generateCostsButtonText,
  enableShapeDrawing = true,
  disableFilters = false,
  maxNumberOfZipcodes,
  zipcodesFormLabel,
  hideMap = false,
  fetchDataCount = defaultFetchDataCount,
  onStateChange,
  children,
}, ref) => {
  const classes = useStyles();
  const theme = useTheme();
  const { appliedFilters } = useDataFilterContext();

  const dataSelectionOverlayRef = React.useRef(null);

  const [campaignDates, setCampaignDates] = React.useState(null);
  const [apnsTotalSelectedCount, setApnsTotalSelectedCount] = React.useState(0);
  const [apnsTotalInAreaCount, setApnsTotalInAreaCount] = React.useState(0);
  const [apnSelectedCounts, setApnSelectedCounts] = React.useState([]);
  const [apnAvailableCounts, setApnAvailableCounts] = React.useState([]);
  const [allApnAvailableCounts, setAllApnAvailableCounts] = React.useState([]);
  const [highPropensityApnAvailableCounts, setHighPropensityApnAvailableCounts] = React.useState([]);
  const [useHighPropensityToSell, setUseHighPropensityToSell] = React.useState(
    preferredUseHighPropensityToSell === true,
  );
  const [hasUserSelectedTargeting, setHasUserSelectedTargeting] = React.useState(
    preferredUseHighPropensityToSell !== null,
  );
  const [estimatedCosts, setEstimatedCosts] = React.useState(null);
  const apnSelectedCountsRef = React.useRef(apnSelectedCounts);
  const apnAvailableCountsRef = React.useRef(apnAvailableCounts);

  React.useEffect(() => {
    apnSelectedCountsRef.current = apnSelectedCounts;
  }, [apnSelectedCounts]);

  React.useEffect(() => {
    apnAvailableCountsRef.current = apnAvailableCounts;
  }, [apnAvailableCounts]);

  const hasFarmLimit = Boolean(apnAvailableCounts.find(({ name, count }) => apnSelectedCounts.find((o) => o.name === name)?.count !== count));
  const highPropensityAvailableCount = React.useMemo(
    () => getCountsTotal(highPropensityApnAvailableCounts),
    [highPropensityApnAvailableCounts],
  );
  const allAvailableCount = React.useMemo(
    () => getCountsTotal(allApnAvailableCounts),
    [allApnAvailableCounts],
  );
  const farmAreaHighPropensityAvailableCount = React.useMemo(
    () => getCountsTotal(highPropensityApnAvailableCounts.filter(({ name }) => name !== addOnPropertiesCountName)),
    [highPropensityApnAvailableCounts],
  );
  const farmAreaAllAvailableCount = React.useMemo(
    () => getCountsTotal(allApnAvailableCounts.filter(({ name }) => name !== addOnPropertiesCountName)),
    [allApnAvailableCounts],
  );

  const showHighPropensityTargeting = isHighPropensityToSellSupported(selectedEventType)
    && (farmAreaAllAvailableCount > 0 || farmAreaHighPropensityAvailableCount > 0);
  const showOverlapWarning = apnAvailableCounts.length > 1
    && apnAvailableCounts.filter(({ name }) => name !== 'Custom Shapes' && name !== addOnPropertiesCountName).length !== apnAvailableCounts.length;

  const applyTargetCounts = React.useCallback((availableCounts, preserveLimits) => {
    const currentApnSelectedCounts = apnSelectedCountsRef.current;
    const currentApnAvailableCounts = apnAvailableCountsRef.current;
    const selectedByName = new Map(currentApnSelectedCounts.map(({ name, count }) => [name, count]));
    const currentAddOnCount = currentApnSelectedCounts.find(({ name }) => name === addOnPropertiesCountName)?.count;
    const currentAddOnAvailableCount = currentApnAvailableCounts.find(({ name }) => name === addOnPropertiesCountName)?.count;
    const availableCountsWithoutAddOns = availableCounts.filter(({ name }) => name !== addOnPropertiesCountName);
    const nextAvailableCounts = currentAddOnAvailableCount !== undefined && !availableCounts.some(({ name }) => name === addOnPropertiesCountName)
      ? [{ name: addOnPropertiesCountName, count: currentAddOnAvailableCount }, ...availableCountsWithoutAddOns]
      : availableCounts;
    const nextSelectedCounts = nextAvailableCounts.map(({ name, count }) => {
      const existingLimit = name === addOnPropertiesCountName ? currentAddOnCount : selectedByName.get(name);
      return {
        name,
        count: preserveLimits && existingLimit !== undefined ? Math.min(existingLimit, count) : count,
      };
    });

    setApnAvailableCounts(nextAvailableCounts);
    setApnSelectedCounts(nextSelectedCounts);
    setApnsTotalSelectedCount(getCountsTotal(nextSelectedCounts));
  }, []);

  const handleTargetingChange = React.useCallback((nextUseHighPropensityToSell, preserveLimits = hasFarmLimit) => {
    setHasUserSelectedTargeting(true);
    setUseHighPropensityToSell(nextUseHighPropensityToSell);
    applyTargetCounts(nextUseHighPropensityToSell ? highPropensityApnAvailableCounts : allApnAvailableCounts, preserveLimits);
  }, [allApnAvailableCounts, applyTargetCounts, hasFarmLimit, highPropensityApnAvailableCounts]);

  React.useEffect(() => {
    const { campaignStartDate, campaignEndDate } = calculateCampaignDates(selectedEventType);
    setCampaignDates([campaignStartDate, campaignEndDate]);
  }, [selectedEventType]);

  React.useEffect(() => {
    if (allApnAvailableCounts.length === 0) return;

    if (!isHighPropensityToSellSupported(selectedEventType)) {
      setUseHighPropensityToSell(false);
      applyTargetCounts(allApnAvailableCounts, false);
      return;
    }

    const shouldUseHighPropensity = hasUserSelectedTargeting ? useHighPropensityToSell : highPropensityAvailableCount > 0;
    setUseHighPropensityToSell(shouldUseHighPropensity);
    applyTargetCounts(shouldUseHighPropensity ? highPropensityApnAvailableCounts : allApnAvailableCounts, false);
  }, [allApnAvailableCounts, applyTargetCounts, hasUserSelectedTargeting, highPropensityApnAvailableCounts, highPropensityAvailableCount, selectedEventType, useHighPropensityToSell]);

  React.useEffect(() => {
    const pricingCampaignApnCount = estimatedNumberOfApnsOverride ?? apnsTotalSelectedCount;
    const campaignPricingData = calculateCampaignPrices(
      selectedEventType,
      pricingCampaignApnCount,
      extraMailOutsCount,
      numApnsAlreadyBeingFarmed,
      customMonthlyRatePerApn,
      renewalPreviousMonthlyRatePerApn,
    );

    setEstimatedCosts({
      ...campaignPricingData,
      isAnnualBilling,
    });
  }, [
    apnsTotalSelectedCount,
    customMonthlyRatePerApn,
    estimatedNumberOfApnsOverride,
    extraMailOutsCount,
    isAnnualBilling,
    numApnsAlreadyBeingFarmed,
    renewalPreviousMonthlyRatePerApn,
    selectedEventType,
  ]);

  React.useEffect(() => {
    onStateChange?.({
      apnAvailableCounts,
      apnSelectedCounts,
      allAvailableCount,
      allApnAvailableCounts,
      apnsTotalInAreaCount,
      apnsTotalSelectedCount,
      farmAreaAllAvailableCount,
      farmAreaHighPropensityAvailableCount,
      hasFarmLimit,
      highPropensityAvailableCount,
      highPropensityApnAvailableCounts,
      showHighPropensityTargeting,
      showOverlapWarning,
      useHighPropensityToSell,
    });
  }, [
    apnAvailableCounts,
    apnSelectedCounts,
    allAvailableCount,
    allApnAvailableCounts,
    apnsTotalInAreaCount,
    apnsTotalSelectedCount,
    farmAreaAllAvailableCount,
    farmAreaHighPropensityAvailableCount,
    hasFarmLimit,
    highPropensityAvailableCount,
    highPropensityApnAvailableCounts,
    onStateChange,
    showHighPropensityTargeting,
    showOverlapWarning,
    useHighPropensityToSell,
  ]);

  React.useImperativeHandle(ref, () => ({
    areDataCountsUpToDate() {
      return dataSelectionOverlayRef.current?.areDataCountsUpToDate?.() ?? true;
    },
    allowRefetchCounts() {
      dataSelectionOverlayRef.current?.allowRefetchCounts?.();
    },
    getSelectedZipcodes() {
      return dataSelectionOverlayRef.current?.getSelectedZipcodes?.() ?? [];
    },
    getSelectionPolygonPaths() {
      return dataSelectionOverlayRef.current?.getSelectionPolygonPaths?.() ?? [];
    },
    getSubmissionData() {
      return {
        propertyFilters: appliedFilters,
        propertyFilterPreset: useHighPropensityToSell ? HIGH_PROPENSITY_TO_SELL_PROPERTY_FILTER_PRESET : undefined,
        polygonPaths: dataSelectionOverlayRef.current?.getSelectionPolygonPaths?.() ?? [],
        zipcodes: dataSelectionOverlayRef.current?.getSelectedZipcodes?.() ?? [],
        apnSelectedCounts,
        isAnnualBilling,
      };
    },
    getState() {
      return {
        apnAvailableCounts,
        apnSelectedCounts,
        apnsTotalInAreaCount,
        apnsTotalSelectedCount,
        hasFarmLimit,
        useHighPropensityToSell,
      };
    },
  }), [apnAvailableCounts, apnSelectedCounts, apnsTotalInAreaCount, apnsTotalSelectedCount, appliedFilters, hasFarmLimit, isAnnualBilling, useHighPropensityToSell]);

  if (!campaignDates || !estimatedCosts) return (<LoadingIndicator />);

  const childProps = {
    apnAvailableCounts,
    apnSelectedCounts,
    apnsTotalInAreaCount,
    apnsTotalSelectedCount,
    setApnAvailableCounts,
    setApnSelectedCounts,
    setAllApnAvailableCounts,
    setApnsTotalInAreaCount,
    setApnsTotalSelectedCount,
    setHighPropensityApnAvailableCounts,
    allApnAvailableCounts,
    highPropensityApnAvailableCounts,
    useHighPropensityToSell,
    hasFarmLimit,
    farmAreaAllAvailableCount,
    farmAreaHighPropensityAvailableCount,
  };

  return (
    <>
      {!hideMap && (
        <div className={classes.map}>
          <DataMap
            dataSelectionOverlayRef={dataSelectionOverlayRef}
            apnSelectedCounts={apnSelectedCounts}
            setApnSelectedCounts={setApnSelectedCounts}
            setApnAvailableCounts={setApnAvailableCounts}
            setAllApnAvailableCounts={setAllApnAvailableCounts}
            setHighPropensityApnAvailableCounts={setHighPropensityApnAvailableCounts}
            setApnsTotalSelectedCount={setApnsTotalSelectedCount}
            setApnsTotalInAreaCount={setApnsTotalInAreaCount}
            allowDataSelection
            filtersModalOptions={filtersModalOptions}
            initPropertyFilters={initPropertyFilters}
            initSelectionPolygonPaths={initSelectionPolygonPaths}
            initSelectedZipcodes={initSelectedZipcodes}
            inputLabelText={inputLabelText}
            maxNumberOfZipcodes={maxNumberOfZipcodes}
            disableFilters={disableFilters}
            enableShapeDrawing={enableShapeDrawing}
            generateCostsButtonText={generateCostsButtonText}
            preferredUseHighPropensityToSell={preferredUseHighPropensityToSell}
            fetchDataCount={fetchDataCount}
            dataCountToastWithFilters
            zipcodesFormLabel={zipcodesFormLabel}
          />
        </div>
      )}

      <div className={classes.infoCards}>
        <CampaignInfoCards
          startDate={campaignDates ? campaignDates[0] : null}
          endDate={campaignDates ? campaignDates[1] : null}
          eventType={selectedEventType}
          campaignCosts={estimatedCosts}
          valuesAreEstimates
          apnAvailableCounts={apnAvailableCounts}
          apnSelectedCounts={apnSelectedCounts}
          setApnSelectedCounts={setApnSelectedCounts}
          apnsTotalSelectedCount={estimatedNumberOfApnsOverride ?? apnsTotalSelectedCount}
          apnsTotalInAreaCount={apnsTotalInAreaCount}
          setApnsTotalSelectedCount={setApnsTotalSelectedCount}
          setIsAnnualBilling={setIsAnnualBilling}
          isCreating
          showHighPropensityTargeting={showHighPropensityTargeting}
          useHighPropensityToSell={useHighPropensityToSell}
          highPropensityAvailableCount={highPropensityAvailableCount}
          allAvailableCount={allAvailableCount}
          showOverlapWarning={showOverlapWarning}
          onTargetingChange={handleTargetingChange}
          apnsFarmingStoppedCount={0}
        />
      </div>

      {isHighPropensityToSellSupported(selectedEventType)
        && farmAreaAllAvailableCount > 0
        && farmAreaHighPropensityAvailableCount === 0 && (
          <DashboardBubble
            backgroundColor={`rgb(${hexToRgb(theme.palette.warning.main)}, 0.1)`}
            icon={<WarningRoundedIcon htmlColor={theme.palette.warning.main} />}
            content="No high propensity APNs are available here. Expand your farm area or use all available APNs."
          />
      )}

      {children?.(childProps)}
    </>
  );
});

export default CampaignTargetingSection;
